import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { MemberCombobox } from "./MemberCombobox";
import { SupportEligibility } from "./SupportEligibility";
import { AlertTriangle, Search, UserPlus, UserMinus, Users } from "lucide-react";

function UserSearch({ orgId, onAdded }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/support-assignments/roster/user-search", { params: { organization_id: orgId, q } })
        .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, orgId]);
  const add = async (u) => {
    try { await api.post("/support-assignments/roster", { organization_id: orgId, user_id: u.user_id }); toast.success(`${u.name} added to the Support Team`); setQ(""); onAdded(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search site users by name or email to add…" className="pl-9 h-10" data-testid="roster-user-search" />
      </div>
      {q.trim().length >= 2 && (
        <div className="mt-2 rounded-md border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="roster-user-results">
          {items.length === 0 && <p className="px-3 py-2 text-sm text-slate-400">No matching users you can see.</p>}
          {items.map((u) => (
            <div key={u.user_id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`roster-user-${u.user_id}`}>
              <div className="min-w-0"><p className="text-sm text-slate-800 truncate">{u.name}</p><p className="text-xs text-slate-500 truncate">{u.email}</p></div>
              {u.on_roster ? <span className="text-xs text-emerald-700">On Support Team</span>
                : u.is_member && !u.eligible_role ? <span className="text-xs text-amber-700" data-testid={`roster-not-eligible-${u.user_id}`}>Role not eligible (candidates, agents, partners)</span>
                : u.is_member ? <Button size="sm" variant="outline" onClick={() => add(u)} data-testid={`roster-add-${u.user_id}`}><UserPlus className="w-3.5 h-3.5 mr-1" />Add</Button>
                  : <span className="text-xs text-amber-700" data-testid={`roster-not-member-${u.user_id}`}>Not in this organization — invite via Admin → Users first</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RemoveDialog({ orgId, member, others, onClose, onDone }) {
  const [impact, setImpact] = useState(null);
  const [mode, setMode] = useState("reassign");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/support-assignments/roster/${member.member_id}/impact`, { params: { organization_id: orgId } })
      .then((r) => setImpact(r.data.items || [])).catch((e) => { toast.error(apiError(e)); setImpact([]); });
  }, [member, orgId]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/support-assignments/roster/${member.member_id}/remove`, { organization_id: orgId, action: impact.length ? mode : null, reassign_to: mode === "reassign" ? target : null });
      toast.success(`${member.name} removed from the Support Team`); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const blocked = impact === null || (impact.length > 0 && mode === "reassign" && !target);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="roster-remove-dialog">
        <DialogHeader>
          <DialogTitle>Remove {member.name} from the Support Team?</DialogTitle>
          <DialogDescription>Their user account, organization membership and assignment history are kept. They won't be available for new assignments here.</DialogDescription>
        </DialogHeader>
        {impact === null ? <p className="text-sm text-slate-400">Checking assignments…</p> : impact.length === 0 ? (
          <p className="text-sm text-slate-600" data-testid="roster-remove-none">No active support assignments.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-800">{impact.length} active assignment{impact.length > 1 ? "s" : ""}:</p>
            <ul className="text-sm text-slate-600 max-h-40 overflow-y-auto space-y-1" data-testid="roster-remove-impact">
              {impact.map((i) => <li key={i.assignment_id}>{i.person_name} ({i.human_id}) · {i.area_label}</li>)}
            </ul>
            <RadioGroup value={mode} onValueChange={setMode} className="gap-2">
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="reassign" data-testid="roster-remove-mode-reassign" />Reassign to another member</label>
              {mode === "reassign" && <MemberCombobox members={others} value={target} onChange={setTarget} testId="roster-remove-target" placeholder="Choose member" />}
              <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="clear" data-testid="roster-remove-mode-clear" />Clear them and leave those areas Not assigned</label>
            </RadioGroup>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || blocked} data-testid="roster-remove-confirm" className="bg-rose-600 hover:bg-rose-700">Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrgSupportRoster() {
  const [params] = useSearchParams();
  const [orgs, setOrgs] = useState(null);
  const [orgId, setOrgId] = useState(params.get("org") || "");
  const [data, setData] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    api.get("/support-assignments/roster/orgs").then((r) => {
      const items = r.data.items || []; setOrgs(items);
      setOrgId((cur) => (items.some((o) => o.id === cur) ? cur : items[0]?.id || ""));
    }).catch(() => setOrgs([]));
  }, []);
  const load = useCallback(() => {
    if (!orgId) return;
    api.get("/support-assignments/roster", { params: { organization_id: orgId } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [orgId]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => {
    if (window.location.hash === "#org-support-team") document.getElementById("org-support-team")?.scrollIntoView({ behavior: "smooth" });
  }, [orgs]);

  const toggle = async (v) => {
    try { await api.put("/support-assignments/settings", { organization_id: orgId, show_in_contacts: v }); setData((d) => ({ ...d, show_in_contacts: v })); toast.success("Setting saved"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (orgs && orgs.length === 0) return null;
  return (
    <section id="org-support-team" className="mb-8 rounded-xl border border-slate-200 bg-white p-5" data-testid="org-support-roster">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-[#006090]" />Organization Support Team</h2>
          <p className="text-sm text-slate-500 mt-0.5 max-w-xl">Existing organization members available for Support Assignments. Being on this list doesn't grant any role or access, and doesn't show them in the candidate My Team directory.</p>
        </div>
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger className="w-full sm:w-[240px] h-10" data-testid="roster-org-select"><SelectValue placeholder="Choose organization" /></SelectTrigger>
          <SelectContent className="bg-white">{(orgs || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {data && (
        <>
          <label className="mt-4 flex items-start gap-3 rounded-lg bg-slate-50 p-3 cursor-pointer">
            <Switch checked={data.show_in_contacts} onCheckedChange={toggle} data-testid="roster-show-in-contacts" />
            <span className="text-sm text-slate-700">Show assigned support members in the "Need Help?" Key Contacts panel
              <span className="block text-xs text-slate-500">Areas without an assigned member keep the topic-default contact. When off, only topic defaults are shown.</span></span>
          </label>
          {data.flagged.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="roster-flagged">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Assignments needing reassignment</p>
              <ul className="mt-1 text-xs text-amber-800 space-y-0.5">
                {data.flagged.map((f, i) => <li key={i}>{f.person_name} ({f.human_id}) · {f.area_label} · {f.member_name}: {f.issue}</li>)}
              </ul>
            </div>
          )}
          <div className="mt-4 divide-y divide-slate-100" data-testid="roster-members">
            {data.items.length === 0 && <p className="text-sm text-slate-400 py-2">No Support Team members yet.</p>}
            {data.items.map((m) => (
              <div key={m.member_id} className="py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid={`roster-member-${m.member_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{m.name}</p>
                  <p className="text-xs text-slate-500 truncate">{m.email || "—"}{m.roles?.length ? ` · ${m.roles.join(", ")}` : ""}</p>
                  {!m.eligible && <p className="text-xs text-amber-700" data-testid={`roster-issue-${m.member_id}`}>Unavailable: {m.issue}</p>}
                  <SupportEligibility orgId={orgId} member={m} onChange={load} />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500" data-testid={`roster-count-${m.member_id}`}>{m.active_assignments ? `${m.active_assignments} active assignment${m.active_assignments > 1 ? "s" : ""}` : "No assignments"}</span>
                  <Button size="sm" variant="outline" onClick={() => setRemoving(m)} data-testid={`roster-remove-${m.member_id}`} className="text-rose-600"><UserMinus className="w-3.5 h-3.5 mr-1" />Remove</Button>
                </div>
              </div>
            ))}
          </div>
          <UserSearch orgId={orgId} onAdded={load} />
        </>
      )}
      {removing && <RemoveDialog orgId={orgId} member={removing} others={data.items.filter((m) => m.eligible && m.member_id !== removing.member_id)}
        onClose={() => setRemoving(null)} onDone={() => { setRemoving(null); load(); }} />}
    </section>
  );
}
